import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { V11UserAvatar } from './V11UserAvatar';
import { V11UserDropdown } from './V11UserDropdown';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';

/**
 * The avatar as drawn in the sidebar (AL-1888): 36px, near-black, white
 * initials. `!` because V11UserAvatar's own size and colour classes are
 * already on the element and this has to win over them.
 */
const AVATAR_CLASSES =
  'h-9! w-9! bg-v11-text-primary! text-[13px]! font-semibold! leading-4 tracking-[0.26px] text-white! dark:bg-v11-surface-hover!';

interface V11UserMenuProps {
  user?: {
    name: string;
    email: string;
    avatar?: string;
  };
  onLogout?: () => void;
  /** In the rail there is room for the avatar and nothing else. */
  collapsed?: boolean;
}

export const V11UserMenu: React.FC<V11UserMenuProps> = ({
  user: propUser,
  onLogout,
  collapsed = false,
}) => {
  const { userData } = useUserProfile();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const user = propUser || userData;
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={toggleDropdown}
        aria-label={user.name}
        className={
          collapsed
            ? 'flex items-center justify-center rounded-full transition cursor-pointer hover:opacity-90 focus:outline-hidden focus:ring-2 focus:ring-v11-accent'
            : 'flex items-center justify-between w-full gap-3 rounded-[8px] bg-[rgba(11,11,11,0.05)] px-3 py-2 text-v11-text-primary hover:bg-[rgba(11,11,11,0.08)] transition cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-v11-accent dark:bg-white/5 dark:hover:bg-white/10'
        }
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="flex min-w-0 items-center gap-3">
          <V11UserAvatar
            src={user.avatar}
            name={user.name}
            size="sm"
            className={AVATAR_CLASSES}
          />

          {!collapsed && (
            <span className="flex min-w-0 flex-col gap-0.5 text-left">
              <span className="block max-w-32 truncate text-[13px] font-semibold leading-[18px]">
                {user.name}
              </span>
              {user.email && (
                <span className="block max-w-32 truncate text-[12px] font-normal leading-4 text-[rgba(11,11,11,0.5)] dark:text-white/50">
                  {user.email}
                </span>
              )}
            </span>
          )}
        </div>

        {!collapsed && (
          <ChevronDown
            className={`h-3.5 w-3.5 shrink-0 text-[rgba(11,11,11,0.5)] transition-transform dark:text-white/50 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        )}
      </button>

      <V11UserDropdown
        userName={user.name}
        userEmail={user.email}
        isOpen={isOpen}
        onClose={closeDropdown}
        onLogout={onLogout || handleLogout}
        placement="top"
      />
    </div>
  );
};
